/**
 * Mobility Portal — Phase 3
 *
 * Vue Employé : parcourir les postes ouverts, voir sa readiness, postuler.
 * Vue Admin   : 3 onglets — Postes (CRUD), Candidatures (validation), Mes candidatures.
 */
import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Briefcase, MapPin, Calendar, Users, Plus, Pencil, Trash2,
  Send, CheckCircle2, XCircle, Clock, Star, AlertTriangle,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const statusBadge = (s) => {
  const map = {
    draft:       'bg-slate-100 text-slate-700',
    open:        'bg-emerald-100 text-emerald-700',
    closed:      'bg-red-100 text-red-700',
    pending:     'bg-amber-100 text-amber-700',
    shortlisted: 'bg-blue-100 text-blue-700',
    accepted:    'bg-emerald-100 text-emerald-700',
    rejected:    'bg-red-100 text-red-700',
  };
  return map[s] || 'bg-slate-100 text-slate-700';
};

const statusLabel = (s) => ({
  draft: 'Brouillon', open: 'Ouvert', closed: 'Clôturé',
  pending: 'En attente', shortlisted: 'Pré-sélectionné', accepted: 'Accepté', rejected: 'Refusé',
}[s] || s);

// ─────────────────────────────────────────────
// Apply Dialog
// ─────────────────────────────────────────────
function ApplyDialog({ posting, open, onOpenChange, onSuccess }) {
  const [motivation, setMotivation] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (motivation.trim().length < 10) {
      toast.error('La lettre de motivation doit faire au moins 10 caractères');
      return;
    }
    try {
      setLoading(true);
      await api.post(`/career-intel/mobility-postings/${posting.id}/apply`, { motivation });
      toast.success('Candidature envoyée');
      setMotivation('');
      onSuccess();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="apply-dialog">
        <DialogHeader>
          <DialogTitle>Postuler à : {posting?.title}</DialogTitle>
          <DialogDescription>
            Décrivez votre motivation et ce qui vous prépare à ce poste.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          rows={6}
          value={motivation}
          onChange={(e) => setMotivation(e.target.value)}
          placeholder="Pourquoi ce poste vous intéresse, et ce que vous y apporteriez..."
          data-testid="apply-motivation"
        />
        <p className="text-xs text-slate-500">{motivation.length} / 10 caractères minimum</p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={loading} data-testid="apply-submit-btn">
            <Send className="w-4 h-4 mr-2" />
            {loading ? 'Envoi...' : 'Envoyer ma candidature'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────
// Posting Form Dialog (Admin)
// ─────────────────────────────────────────────
function PostingFormDialog({ open, onOpenChange, posting, profiles, onSaved }) {
  const empty = {
    title: '', targetJobProfileId: '', description: '', seats: 1,
    deadline: '', status: 'draft', requirements: '', contractType: '',
  };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setForm(posting ? { ...empty, ...posting } : empty);
  }, [posting, open]);

  const save = async () => {
    if (!form.title || !form.targetJobProfileId) {
      toast.error('Titre et fiche de poste cible requis');
      return;
    }
    try {
      setLoading(true);
      if (posting?.id) {
        await api.put(`/career-intel/mobility-postings/${posting.id}`, form);
        toast.success('Poste mis à jour');
      } else {
        await api.post('/career-intel/mobility-postings', form);
        toast.success('Poste créé');
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="posting-form-dialog">
        <DialogHeader>
          <DialogTitle>{posting?.id ? 'Modifier le poste' : 'Nouveau poste ouvert'}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
          <div className="md:col-span-2">
            <Label>Intitulé du poste *</Label>
            <Input
              value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="ex: Chef de projet adjoint DSI" data-testid="posting-title-input"
            />
          </div>
          <div>
            <Label>Fiche de poste cible *</Label>
            <Select value={form.targetJobProfileId} onValueChange={(v) => setForm({ ...form, targetJobProfileId: v })}>
              <SelectTrigger data-testid="posting-profile-select"><SelectValue placeholder="Choisir une fiche" /></SelectTrigger>
              <SelectContent>
                {profiles.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Statut</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="posting-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Brouillon</SelectItem>
                <SelectItem value="open">Ouvert (visible employés)</SelectItem>
                <SelectItem value="closed">Clôturé</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Nombre de places</Label>
            <Input type="number" min={1} value={form.seats || 1}
              onChange={(e) => setForm({ ...form, seats: parseInt(e.target.value, 10) || 1 })} />
          </div>
          <div>
            <Label>Date limite</Label>
            <Input type="date" value={form.deadline || ''}
              onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
          </div>
          <div>
            <Label>Type de contrat</Label>
            <Input value={form.contractType || ''}
              onChange={(e) => setForm({ ...form, contractType: e.target.value })} placeholder="CDI, CDD, ..." />
          </div>
          <div className="md:col-span-2">
            <Label>Description</Label>
            <Textarea rows={3} value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Missions, contexte, équipe..." />
          </div>
          <div className="md:col-span-2">
            <Label>Exigences</Label>
            <Textarea rows={2} value={form.requirements || ''}
              onChange={(e) => setForm({ ...form, requirements: e.target.value })}
              placeholder="Diplômes, expérience minimum..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={save} disabled={loading} data-testid="posting-save-btn">
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────
// Posting Card
// ─────────────────────────────────────────────
function PostingCard({ posting, onApply, onEdit, onDelete, isAdmin }) {
  const ready = posting.myReadiness;
  const alreadyApplied = !!posting.myApplication;

  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`posting-card-${posting.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge className={statusBadge(posting.status)}>{statusLabel(posting.status)}</Badge>
              {posting.contractType && <Badge variant="outline" className="text-xs">{posting.contractType}</Badge>}
              {isAdmin && (
                <Badge variant="outline" className="text-xs">
                  <Users className="w-3 h-3 mr-1" />{posting.applicationsCount || 0} candidature(s)
                </Badge>
              )}
            </div>
            <h3 className="font-semibold text-slate-900 truncate">{posting.title}</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Fiche cible : {posting.targetProfile?.name || '—'}
            </p>
          </div>
          {isAdmin && (
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => onEdit(posting)} data-testid={`edit-posting-${posting.id}`}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onDelete(posting)} data-testid={`delete-posting-${posting.id}`}>
                <Trash2 className="w-4 h-4 text-red-600" />
              </Button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-3 text-xs text-slate-500 mb-3">
          {posting.seats && <span><Users className="w-3 h-3 inline mr-1" />{posting.seats} place(s)</span>}
          {posting.deadline && <span><Calendar className="w-3 h-3 inline mr-1" />Avant {posting.deadline}</span>}
        </div>

        {posting.description && (
          <p className="text-sm text-slate-600 mb-3 line-clamp-2">{posting.description}</p>
        )}

        {ready && ready.totalSkills > 0 && (
          <div className="mb-3 p-2 rounded bg-indigo-50">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-slate-600">Ma readiness</span>
              <strong className={ready.readiness >= 75 ? 'text-emerald-600' : ready.readiness >= 50 ? 'text-amber-600' : 'text-red-600'}>
                {ready.readiness}%
              </strong>
            </div>
            <Progress value={ready.readiness} className="h-1.5" />
            {ready.criticalBlocking && (
              <p className="text-xs text-red-700 mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />Compétences critiques manquantes
              </p>
            )}
          </div>
        )}

        {!isAdmin && posting.status === 'open' && (
          alreadyApplied ? (
            <div className="text-xs p-2 rounded bg-emerald-50 text-emerald-700 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Candidature envoyée — {statusLabel(posting.myApplication.status)}
            </div>
          ) : (
            <Button size="sm" className="w-full" onClick={() => onApply(posting)} data-testid={`apply-btn-${posting.id}`}>
              <Send className="w-3.5 h-3.5 mr-2" />Postuler
            </Button>
          )
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────
// Applications List (Admin)
// ─────────────────────────────────────────────
function ApplicationsAdminTab() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchApps = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/career-intel/mobility-applications');
      setApps(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  const decide = async (id, decision) => {
    try {
      await api.put(`/career-intel/mobility-applications/${id}/decision`, { decision });
      toast.success('Décision enregistrée');
      fetchApps();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const filtered = apps.filter(a => filter === 'all' || a.status === filter);

  if (loading) return <p className="text-sm text-slate-400 text-center py-8">Chargement...</p>;
  if (apps.length === 0) return (
    <div className="text-center py-12 text-sm text-slate-500">
      Aucune candidature pour le moment.
    </div>
  );

  return (
    <div className="space-y-3" data-testid="applications-admin-tab">
      <div className="flex gap-2 items-center flex-wrap">
        {['all', 'pending', 'shortlisted', 'accepted', 'rejected'].map(s => (
          <Button key={s} size="sm" variant={filter === s ? 'default' : 'outline'} onClick={() => setFilter(s)}>
            {s === 'all' ? 'Toutes' : statusLabel(s)} ({s === 'all' ? apps.length : apps.filter(a => a.status === s).length})
          </Button>
        ))}
      </div>
      {filtered.map(a => (
        <Card key={a.id} data-testid={`application-card-${a.id}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge className={statusBadge(a.status)}>{statusLabel(a.status)}</Badge>
                  <span className="text-xs text-slate-500">{new Date(a.createdAt).toLocaleDateString('fr-FR')}</span>
                </div>
                <p className="font-semibold text-slate-900">
                  {a.employee?.firstName} {a.employee?.lastName}
                </p>
                <p className="text-xs text-slate-500">
                  {a.employee?.jobTitle || '—'} → <strong>{a.posting?.title}</strong>
                </p>
                {a.snapshot?.readiness != null && (
                  <p className="text-xs text-slate-600 mt-1">
                    Readiness snapshot : <strong>{a.snapshot.readiness}%</strong>
                    {a.snapshot.criticalBlocking && <span className="text-red-600 ml-2">⚠ Critiques bloquantes</span>}
                  </p>
                )}
                <p className="text-sm text-slate-700 mt-2 italic line-clamp-3">"{a.motivation}"</p>
                {a.rhComment && (
                  <p className="text-xs text-slate-500 mt-1">Note RH : {a.rhComment}</p>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                {a.status !== 'shortlisted' && (
                  <Button size="sm" variant="outline" onClick={() => decide(a.id, 'shortlisted')}
                    data-testid={`shortlist-${a.id}`}>
                    <Star className="w-3.5 h-3.5 mr-1.5" />Pré-sélectionner
                  </Button>
                )}
                {a.status !== 'accepted' && (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => decide(a.id, 'accepted')}
                    data-testid={`accept-${a.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />Accepter
                  </Button>
                )}
                {a.status !== 'rejected' && (
                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => decide(a.id, 'rejected')}
                    data-testid={`reject-${a.id}`}>
                    <XCircle className="w-3.5 h-3.5 mr-1.5" />Refuser
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────
export default function MobilityPortalPage() {
  const { user } = useAuth();
  const isAdmin = ['Admin', 'SuperAdmin'].includes(user?.role);

  const [postings, setPostings] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyTarget, setApplyTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(isAdmin ? 'postings' : 'open');
  const [myApps, setMyApps] = useState([]);

  const fetchPostings = useCallback(async () => {
    try {
      setLoading(true);
      const [pRes, profRes] = await Promise.all([
        api.get('/career-intel/mobility-postings'),
        api.get('/career-intel/job-profiles/lite').catch(() => ({ data: { items: [] } })),
      ]);
      setPostings(pRes.data?.items || []);
      setProfiles(profRes.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  }, []);

  const fetchMyApps = useCallback(async () => {
    try {
      const res = await api.get('/career-intel/mobility-applications');
      setMyApps(res.data?.items || []);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchPostings();
    if (!isAdmin) fetchMyApps();
  }, [fetchPostings, fetchMyApps, isAdmin]);

  const handleDelete = async (p) => {
    if (!window.confirm(`Supprimer le poste "${p.title}" et toutes ses candidatures ?`)) return;
    try {
      await api.delete(`/career-intel/mobility-postings/${p.id}`);
      toast.success('Poste supprimé');
      fetchPostings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="mobility-portal-page">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Briefcase className="w-6 h-6 text-indigo-600" />
              Mobilité interne
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {isAdmin
                ? 'Gérez les postes ouverts et examinez les candidatures internes.'
                : 'Découvrez les postes ouverts dans l\'entreprise et postulez en interne.'}
            </p>
          </div>
          {isAdmin && (
            <Button onClick={() => { setEditTarget(null); setFormOpen(true); }} data-testid="new-posting-btn">
              <Plus className="w-4 h-4 mr-2" />Nouveau poste
            </Button>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100">
            {isAdmin ? (
              <>
                <TabsTrigger value="postings" data-testid="tab-postings">
                  <Briefcase className="w-4 h-4 mr-2" />Postes ({postings.length})
                </TabsTrigger>
                <TabsTrigger value="applications" data-testid="tab-applications">
                  <Users className="w-4 h-4 mr-2" />Candidatures
                </TabsTrigger>
              </>
            ) : (
              <>
                <TabsTrigger value="open" data-testid="tab-open">
                  <Briefcase className="w-4 h-4 mr-2" />Postes ouverts
                </TabsTrigger>
                <TabsTrigger value="mine" data-testid="tab-mine">
                  <Clock className="w-4 h-4 mr-2" />Mes candidatures ({myApps.length})
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Postes (Admin et Employé) */}
          {(isAdmin ? activeTab === 'postings' : activeTab === 'open') && (
            <TabsContent value={isAdmin ? 'postings' : 'open'} className="mt-4">
              {loading ? (
                <p className="text-sm text-slate-400 text-center py-8">Chargement...</p>
              ) : postings.length === 0 ? (
                <div className="text-center py-12 text-sm text-slate-500">
                  <Briefcase className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                  {isAdmin ? "Aucun poste pour le moment. Créez votre premier poste ouvert." : "Aucun poste ouvert pour le moment."}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {postings.map(p => (
                    <PostingCard
                      key={p.id} posting={p} isAdmin={isAdmin}
                      onApply={async (post) => {
                        const detailed = await api.get(`/career-intel/mobility-postings/${post.id}`);
                        setApplyTarget(detailed.data);
                      }}
                      onEdit={(post) => { setEditTarget(post); setFormOpen(true); }}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          {/* Candidatures Admin */}
          {isAdmin && activeTab === 'applications' && (
            <TabsContent value="applications" className="mt-4">
              <ApplicationsAdminTab />
            </TabsContent>
          )}

          {/* Mes candidatures (Employé) */}
          {!isAdmin && activeTab === 'mine' && (
            <TabsContent value="mine" className="mt-4 space-y-3">
              {myApps.length === 0 ? (
                <div className="text-center py-12 text-sm text-slate-500">
                  Vous n'avez pas encore postulé.
                </div>
              ) : myApps.map(a => (
                <Card key={a.id} data-testid={`my-app-${a.id}`}>
                  <CardContent className="p-4 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <Badge className={statusBadge(a.status)}>{statusLabel(a.status)}</Badge>
                      <p className="font-semibold text-slate-900 mt-1">{a.posting?.title}</p>
                      <p className="text-xs text-slate-500">Candidature du {new Date(a.createdAt).toLocaleDateString('fr-FR')}</p>
                      {a.snapshot?.readiness != null && (
                        <p className="text-xs text-slate-600 mt-1">Readiness lors de la candidature : <strong>{a.snapshot.readiness}%</strong></p>
                      )}
                      {a.rhComment && (
                        <p className="text-xs text-slate-700 mt-1 italic bg-slate-50 p-2 rounded">RH : {a.rhComment}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>
          )}
        </Tabs>

        <ApplyDialog
          posting={applyTarget}
          open={!!applyTarget}
          onOpenChange={(v) => { if (!v) setApplyTarget(null); }}
          onSuccess={() => { fetchPostings(); fetchMyApps(); }}
        />
        <PostingFormDialog
          posting={editTarget}
          profiles={profiles}
          open={formOpen}
          onOpenChange={setFormOpen}
          onSaved={fetchPostings}
        />
      </div>
    </DashboardLayout>
  );
}
